import { FilterGroup, FilterGroupType } from '@fe/core';
import { FeMap, FeMapPlugin } from '@fe/maplibre-extensions';
import {
    AllLayoutProperties,
    AllPaintProperties,
    ExpressionFilterSpecification,
    ExpressionSpecification,
    FilterSpecification,
    Map as MaplibreMap
} from 'maplibre-gl';
import { NavaidRingsLayer } from './navaid-rings-layer';
import {
    AirportRunwayData,
    IUDViewAirspaceModel,
    IUDViewAirwayModel,
    IUDViewFilterGroupModel,
    IUDViewFixesModel,
    IUDViewHoldingModel,
    IUDViewNavaidsModel,
    IUDViewStaticDataModel,
    IUDViewSUAModel,
    TerrainShadingSettings
} from './navdata-types';
import { TerrainShadingLayer } from './terrain-shading-layer';

// Airspace types excluding 'fir' and 'uir'
const airspaceTypes = {
    classA: ['A'],
    classB: ['B'],
    classC: ['C'],
    classD: ['D'],
    classE: ['E'],
    classF: ['F'],
    classG: ['G'],
    other: ['N']
} satisfies Partial<Record<keyof IUDViewAirspaceModel, [string]>>;

const fixeFilterExpressions = {
    latLon: ['==', ['get', 'type'], 'L'],
    nrs: ['==', ['get', 'type'], 'N'],
    aacaA_SUA: ['==', ['get', 'type'], 'A'],
    intersection: ['==', ['get', 'type'], 'I'],
    rnav: ['==', ['get', 'type'], 'R'],
    vfr: ['==', ['get', 'type'], 'V'],
    other: ['==', ['get', 'type'], 'O']
    // terminal moved to visibility
} satisfies Partial<Record<keyof IUDViewFixesModel, ExpressionSpecification>>;

const holdingTypes = {
    lowAltitude: 2,
    highAltitude: 1,
    sid: 3,
    star: 4,
    approach: 5,
    missedApproach: 6,
    other: 0
} satisfies Partial<Record<keyof IUDViewHoldingModel, number>>;

const suaTypes = {
    alert: 'A',
    caution: 'S',
    danger: 'D',
    militaryArea: 'M',
    prohibited: 'P',
    restricted: 'R',
    traning: 'T',
    warning: 'W',
    unkown: 'U'
} satisfies Partial<Record<keyof IUDViewSUAModel, string>>;

const airportFilterExpressions = {
    tower: ['all', ['==', ['get', 'apttype'], 'A'], ['==', ['get', 'ct'], true]],
    noTower: ['all', ['==', ['get', 'apttype'], 'A'], ['==', ['get', 'ct'], false]],
    heliport: ['==', ['get', 'apttype'], 'H'],
    seaplane: ['==', ['get', 'apttype'], 'S'],
    others: ['!', ['in', ['get', 'apttype'], ['literal', ['A', 'H', 'S']]]]
} satisfies Record<string, ExpressionSpecification>;

const naviadFilterExpressions = {
    ndb: ['==', ['get', 'type'], 3],
    vor: ['==', ['get', 'type'], 4],
    voR_DME: ['==', ['get', 'type'], 5],
    vortac: ['==', ['get', 'type'], 6],
    tacan: ['==', ['get', 'type'], 7],
    dme: ['==', ['get', 'type'], 14],
    ndB_DME: ['==', ['get', 'type'], 11]
} satisfies Partial<Record<keyof IUDViewNavaidsModel, ExpressionSpecification>>;

const airwayFilterExpressions = {
    low: ['in', ['get', 'level'], ['literal', ['L', 'B']]],
    high: ['in', ['get', 'level'], ['literal', ['H', 'B']]]
} satisfies Partial<Record<keyof IUDViewAirwayModel, ExpressionSpecification>>;

export class NavDataFilter implements FeMapPlugin {
    private airportRunwayUsageMap: Map<string, Omit<AirportRunwayData, 'recordId'>> = new Map();
    private map: MaplibreMap | null = null;
    private myAirports: string[] = [];
    private readonly navaidRings = new NavaidRingsLayer();
    private readonly terrainShading = new TerrainShadingLayer();
    private navData: IUDViewStaticDataModel | null = null;
    private airportFilter: ExpressionFilterSpecification = false;
    private filterGroups: Partial<Record<string, FilterGroup>> = {};

    onAdd = (map: FeMap) => {
        this.map = map;
        this.navaidRings.onAdd(map);
        this.terrainShading.onAdd(map);
    };

    onRemove = (map: FeMap) => {
        this.map = null;
        this.navData = null;
        this.airportRunwayUsageMap.clear();
        this.navaidRings.onRemove(map);
        this.terrainShading.onRemove(map);
    };

    setTerrainShading(settings: TerrainShadingSettings) {
        this.terrainShading.update(settings);
    }

    setMyAirports(airports: string[]) {
        this.myAirports = airports;
    }

    setFilterGroups(filterGroups: Record<FilterGroupType, FilterGroup[]>) {
        this.filterGroups = Object.fromEntries(
            Object.values(filterGroups)
                .flat()
                .map((x) => [x.id, x])
        );
        this.navaidRings.setFilterGroups(this.filterGroups);
    }

    setData(navData: IUDViewStaticDataModel): void {
        this.navData = navData;
        if (this.map) {
            this.applyFilter();
        }
    }

    applyFilter(): void {
        if (this.navData == null || !this.map) {
            return;
        }
        this.setAirport();
        this.setAirspace();
        this.setAirways();
        this.setFixes();
        this.setFlightServiceStations();
        this.setHoldingPatterns();
        this.setGridMora();
        this.setNavaids();
        this.setOverwaterAreas();
        this.setSids();
        this.setStars();
        this.setSua();
        this.setTimeZone();
        this.setWeatherStations();
        this.navaidRings.apply(this.navData!);
    }

    getAirportFilter() {
        return this.airportFilter;
    }

    private joinedIdExpression(first: string, second: string): any {
        const a = ['to-string', ['coalesce', ['get', first], '']];
        const b = ['to-string', ['coalesce', ['get', second], '']];
        return ['case', ['==', a, ''], b, ['==', b, ''], a, ['concat', a, '/', b]];
    }

    private setAirport(): void {
        if (!this.map) {
            return;
        }

        const size = this.navData!.airport.settings.fontSize;
        this.setLayoutProperty('airport', 'visibility', this.toVisibility(this.navData!.airport.show));
        this.setLayoutProperty('airport', 'text-size', size);
        this.setLayoutProperty('airport', 'text-offset', [0, 8 / size]);

        if (this.map.getLayer('fe-airport-range-rings')) {
            this.setLayoutProperty('fe-airport-range-rings', 'visibility', this.toVisibility(this.navData!.airport.show));
        }
        if (this.map.getLayer('fe-airport-range-rings-fill')) {
            this.setLayoutProperty('fe-airport-range-rings-fill', 'visibility', this.toVisibility(this.navData!.airport.show));
        }

        if (this.navData!.airport.show) {
            this.airportFilter = this.setAirportFilter();
        } else {
            this.airportFilter = false;
            this.setLayoutProperty('deicing', 'visibility', 'none');
            this.setLayoutProperty('runway', 'visibility', 'none');

            if (this.map.getLayer('runway-center-arrow-left') && this.map.getLayer('runway-center-arrow-right')) {
                this.setLayoutProperty('runway-center-arrow-left', 'visibility', 'none');
                this.setLayoutProperty('runway-center-arrow-right', 'visibility', 'none');
            }
        }
    }

    private setAirspace(): void {
        if (!this.map) {
            return;
        }

        const visibility = this.toVisibility(this.navData!.airspace.show);
        this.setLayoutProperty('airspace_border', 'visibility', visibility);
        this.setLayoutProperty('airspace_border2', 'visibility', visibility);
        this.setLayoutProperty('airspace_fill', 'visibility', visibility);
        this.setLayoutProperty('airspace_label', 'visibility', visibility);
        this.setLayoutProperty('airspace_label2', 'visibility', visibility);
        if (this.navData!.airspace.show) {
            this.setAirspaceFilter();
        }
    }

    private setAirways(): void {
        if (!this.map) {
            return;
        }

        const visibility = this.toVisibility(this.navData!.airway.show);
        const textSize = this.navData!.airway.settings.fontSize;
        this.setLayoutProperty('airway_point', 'text-size', textSize);
        this.setLayoutProperty('airway_point', 'text-offset', [6 / textSize, 10 / textSize]);
        this.setLayoutProperty('airway_label', 'text-size', textSize);
        this.setLayoutProperty('airway', 'visibility', visibility);
        this.setLayoutProperty('airway_label', 'visibility', visibility);
        this.setLayoutProperty('airway_point', 'visibility', visibility);
        if (this.navData!.airway.show) {
            this.setAirwayFilter();
        }
    }

    private setFixes(): void {
        if (!this.map) {
            return;
        }

        const fontSize = this.navData!.fixes.settings.fontSize;

        this.setLayoutProperty('fix', 'text-size', fontSize);
        this.setLayoutProperty('fix', 'text-offset', [0, 8 / fontSize]);

        this.setLayoutProperty('fix_t', 'text-size', fontSize);
        this.setLayoutProperty('fix_t', 'text-offset', [0, 8 / fontSize]);

        this.setLayoutProperty('fix', 'visibility', this.toVisibility(this.navData!.fixes.show));
        this.setLayoutProperty(
            'fix_t',
            'visibility',
            this.toVisibility(this.navData!.fixes.show && (this.navData!.fixes.terminal.show || !!this.navData!.fixes.filterGroup?.show))
        );
        if (this.navData!.fixes.show) {
            this.setFixesFilter();
        }
    }

    private setFlightServiceStations(): void {
        if (!this.map) {
            return;
        }

        this.setLayoutProperty('fss', 'visibility', this.toVisibility(this.navData!.flightServiceStation.show));
        if (this.navData!.flightServiceStation.show) {
            this.setFlightServiceStationsFilter();
        }
    }

    private setHoldingPatterns(): void {
        if (!this.map) {
            return;
        }

        const visibility = this.toVisibility(this.navData!.holding.show);
        this.setLayoutProperty('holding', 'visibility', visibility);
        this.setLayoutProperty('holding_label', 'visibility', visibility);
        if (this.navData!.holding.show) {
            this.setHoldingPatternsFilter();
        }
    }

    private setGridMora(): void {
        if (!this.map) {
            return;
        }

        const visibility = this.toVisibility(this.navData!.gridMora.show);
        this.setLayoutProperty('gridmora1', 'visibility', visibility);
        this.setLayoutProperty('gridmora1_label', 'visibility', visibility);
        this.setLayoutProperty('gridmora1_label2', 'visibility', visibility);
        this.setLayoutProperty('gridmora5', 'visibility', visibility);
        this.setLayoutProperty('gridmora5_label', 'visibility', visibility);
        this.setLayoutProperty('gridmora5_label2', 'visibility', visibility);
    }

    private setNavaids(): void {
        if (!this.map) {
            return;
        }

        const textSize = this.navData!.navaids.settings.fontSize;
        const visibility = this.toVisibility(this.navData!.navaids.show);

        this.setLayoutProperty('navaid', 'text-size', textSize);
        this.setLayoutProperty('navaid', 'text-offset', [0, 8 / textSize]);

        this.setLayoutProperty('navaid_rose', 'text-size', textSize);
        this.setLayoutProperty('navaid_rose', 'text-offset', [0, 8 / textSize]);

        this.setLayoutProperty('navaid', 'visibility', visibility);
        this.setLayoutProperty('navaid_rose', 'visibility', visibility);
        if (this.navData!.navaids.show) {
            this.setNavaidsFilter();
        }
    }

    private setOverwaterAreas(): void {
        const visibility = this.toVisibility(this.navData!.overwaterAreas.show);
        const lineColor = this.navData!.overwaterAreas.lineColor?.trim();

        this.setLayoutProperty('overwater', 'visibility', visibility);
        if (lineColor) {
            this.map!.setGlobalStateProperty('feNav_line-color_overwater', lineColor);
        }
    }

    private setSids(): void {
        if (!this.map) {
            return;
        }

        const visibility = this.toVisibility(this.navData!.sids.show);
        this.setLayoutProperty('sid', 'visibility', visibility);
        this.setLayoutProperty('sid_point', 'visibility', visibility);
        this.setLayoutProperty('sid_arrow', 'visibility', visibility);

        if (this.navData!.sids.show) {
            this.setSidsFilter();
        }
    }

    private setStars(): void {
        if (!this.map) {
            return;
        }

        const visibility = this.toVisibility(this.navData!.stars.show);
        this.setLayoutProperty('star', 'visibility', visibility);
        this.setLayoutProperty('star_point', 'visibility', visibility);
        this.setLayoutProperty('star_arrow', 'visibility', visibility);

        if (this.navData!.stars.show) {
            this.setStarsFilter();
        }
    }

    private setSua(): void {
        if (!this.map) {
            return;
        }

        // Handle only static SUA. The dynamic is handled by WMS
        const showSua = this.navData!.sua.show && this.navData!.sua.static;
        const visibility = this.toVisibility(showSua);
        this.setLayoutProperty('sua_border', 'visibility', visibility);
        this.setLayoutProperty('sua_band', 'visibility', visibility);
        this.setLayoutProperty('sua_label', 'visibility', visibility);
        if (showSua) {
            this.setSuaFilter();
        }
    }

    private setTimeZone(): void {
        if (!this.map) {
            return;
        }

        this.setLayoutProperty('timezone', 'visibility', this.toVisibility(this.navData!.timeZone.show));
    }

    private setWeatherStations(): void {
        if (!this.map) {
            return;
        }

        this.setLayoutProperty('wstation', 'visibility', this.toVisibility(this.navData!.weatherStation.show));
        if (this.navData!.weatherStation.show) {
            this.weatherStationsFilter();
        }
    }

    private setAirportFilter() {
        if (!this.map) {
            return false;
        }

        const { myAirports, tower, noTower, heliport, seaplane, others, filterGroup, runways, deicing, settings } = this.navData!.airport;

        const filterExpressions: ExpressionSpecification[] = [
            tower.show && airportFilterExpressions.tower,
            noTower.show && airportFilterExpressions.noTower,
            heliport.show && airportFilterExpressions.heliport,
            seaplane.show && airportFilterExpressions.seaplane,
            others.show && airportFilterExpressions.others
        ].filter((x) => !!x);

        const colorExpressions = [
            tower.show && tower.color ? [airportFilterExpressions.tower, tower.color] : null,
            noTower.show && noTower.color ? [airportFilterExpressions.noTower, noTower.color] : null,
            heliport.show && heliport.color ? [airportFilterExpressions.heliport, heliport.color] : null,
            seaplane.show && seaplane.color ? [airportFilterExpressions.seaplane, seaplane.color] : null,
            others.show && others.color ? [airportFilterExpressions.others, others.color] : null
        ].filter((x) => !!x) as [ExpressionSpecification, string][];

        // Dynamic filters
        if (myAirports.show) {
            const myAirportFilter: ExpressionSpecification = [
                'any',
                ['in', ['get', 'icaoid'], ['literal', this.myAirports]],
                ['in', ['get', 'iataid'], ['literal', this.myAirports]],
                ['in', ['get', 'faaid'], ['literal', this.myAirports]]
            ];

            filterExpressions.push(myAirportFilter);

            if (myAirports.color) {
                colorExpressions.unshift([myAirportFilter, myAirports.color]);
            }
        }

        if (filterGroup?.show) {
            this.addFilterGroupToExpressions(
                filterGroup,
                (values) => [
                    'any',
                    ['in', ['get', 'icaoid'], ['literal', values]],
                    ['in', ['get', 'iataid'], ['literal', values]],
                    ['in', ['get', 'faaid'], ['literal', values]]
                ],
                filterExpressions,
                colorExpressions
            );
        }

        const filter: FilterSpecification = ['any', ...filterExpressions];

        const airportLabelType = settings.airportLabelType;
        let textField: AllLayoutProperties['text-field'];

        if (airportLabelType === 'faa') {
            textField = ['coalesce', ['get', 'faaid'], ['get', 'icaoid']];
        } else if (airportLabelType === 'icao') {
            textField = ['get', 'icaoid'];
        } else if (airportLabelType === 'iataIcao') {
            textField = this.joinedIdExpression('iataid', 'icaoid');
        } else if (airportLabelType === 'icaoIata') {
            textField = this.joinedIdExpression('icaoid', 'iataid');
        } else {
            textField = ['get', 'iataid'];
        }

        this.setLayoutProperty('airport', 'text-field', textField);
        this.setPaintProperty('airport', 'icon-color', this.toColorExpression(colorExpressions, ['global-state', 'feNav_icon-color']));

        this.setLayoutProperty('runway', 'visibility', this.toVisibility(runways));
        this.setLayoutProperty('deicing', 'visibility', this.toVisibility(deicing));
        this.setLayoutProperty('runway-center-arrow-left', 'visibility', this.toVisibility(runways && settings.directionIndicator));
        this.setLayoutProperty('runway-center-arrow-right', 'visibility', this.toVisibility(runways && settings.directionIndicator));

        if (settings.rangeRingsEnabled) {
            this.setPaintProperty('fe-airport-range-rings', 'line-color', settings.rangeRingsLineColor);
            this.setPaintProperty(
                'fe-airport-range-rings',
                'line-opacity',
                settings.rangeRingsLineStyle === 'solid' ? settings.rangeRingsOpacity : 0
            );
            this.setPaintProperty('fe-airport-range-rings-fill', 'fill-color', settings.rangeRingsFillColor);
            this.setPaintProperty(
                'fe-airport-range-rings-fill',
                'fill-opacity',
                settings.rangeRingsFillStyle === 'solid' ? settings.rangeRingsOpacity : 0
            );
        }

        this.setFilter('airport', filter);

        return filter;
    }

    private setAirspaceFilter(): void {
        if (!this.map) {
            return;
        }

        let exceptOfFirAndUir: FilterSpecification = false;
        const { filterGroup } = this.navData!.airspace;
        for (const [key, value] of Object.entries(airspaceTypes)) {
            if (this.navData!.airspace[key as keyof IUDViewAirspaceModel]) {
                exceptOfFirAndUir = ['any', exceptOfFirAndUir, ['match', ['get', 'class'], value, true, false]];
            }
        }

        // Fir and uir are used only for airspace_label2 and airspace_border
        let firAndUir: FilterSpecification = false;
        if (this.navData!.airspace.fir) {
            firAndUir = ['any', firAndUir, ['match', ['get', 'class'], ['T', 'V'], true, false]];
        }

        if (this.navData!.airspace.uir) {
            firAndUir = ['any', firAndUir, ['match', ['get', 'class'], ['U', 'V'], true, false]];
        }

        // All
        let filterAll: FilterSpecification = ['any', exceptOfFirAndUir, firAndUir];

        // FilterGroups
        if (filterGroup?.show) {
            const values = this.getFilterGroupFlatMembers(filterGroup.ids);
            filterAll = ['any', filterAll, ['in', ['get', 'id'], ['literal', values]]];
        }

        // Not for class C and D
        const notCD: FilterSpecification = ['all', filterAll, ['match', ['get', 'class'], ['C', 'D'], false, true]];

        // Only for class C and D
        const onlyCD: FilterSpecification = ['all', filterAll, ['match', ['get', 'class'], ['C', 'D'], true, false]];

        // Only for class B and C
        const onlyBC: FilterSpecification = ['all', filterAll, ['match', ['get', 'class'], ['B', 'C'], true, false]];

        this.setFilter('airspace_border', notCD);
        this.setFilter('airspace_border2', onlyCD);
        this.setFilter('airspace_fill', onlyBC);
        this.setFilter('airspace_label', exceptOfFirAndUir);
        this.setFilter('airspace_label2', firAndUir);
    }

    private setAirwayFilter(): void {
        if (!this.map) {
            return;
        }

        const { high, low, filterGroup } = this.navData!.airway;
        const filterExpressions: ExpressionSpecification[] = [];
        const colorExpressions: [ExpressionSpecification, string][] = [];

        if (low.show) {
            filterExpressions.push(airwayFilterExpressions.low);
            if (low.color) {
                colorExpressions.push([airwayFilterExpressions.low, low.color]);
            }
        }

        if (high.show) {
            filterExpressions.push(airwayFilterExpressions.high);
            if (high.color) {
                colorExpressions.push([airwayFilterExpressions.high, high.color]);
            }
        }

        if (filterGroup?.show) {
            this.addFilterGroupToExpressions(filterGroup, ['get', 'id'], filterExpressions, colorExpressions);
        }

        const filter = ['any', ...filterExpressions] as FilterSpecification;
        const color = this.toColorExpression(colorExpressions, [
            'match',
            ['slice', ['get', 'id'], 0, 1],
            ['T', 'Q'],
            'rgba(0, 0, 160, 1)',
            ['global-state', 'feNav_line-color_awy_def']
        ]);

        this.setPaintProperty('airway', 'line-color', color);
        this.setPaintProperty('airway_label', 'icon-color', color);

        this.setFilter('airway', filter);
        this.setFilter('airway_label', filter);
        this.setFilter('airway_point', filter);
    }

    private setFixesFilter(): void {
        if (!this.map) {
            return;
        }

        const { filterGroup } = this.navData!.fixes;
        const filterExpressions: ExpressionSpecification[] = [];
        const colorExpressions: [ExpressionSpecification, string][] = [];
        for (const [key, expression] of Object.entries(fixeFilterExpressions)) {
            const subType = this.navData!.fixes[key as keyof typeof fixeFilterExpressions];
            if (subType.show) {
                filterExpressions.push(expression);
                if (subType.color) {
                    colorExpressions.push([expression, subType.color]);
                }
            }
        }

        if (filterGroup?.show) {
            this.addFilterGroupToExpressions(filterGroup, ['get', 'id'], filterExpressions, colorExpressions);
        }

        const usageExpressions: ExpressionSpecification[] = [];

        // L=low, H=high, B=both
        if (this.navData!.fixes.low) {
            usageExpressions.push(['==', ['get', 'usage'], 'L']);
        }

        if (this.navData!.fixes.high) {
            usageExpressions.push(['==', ['get', 'usage'], 'H']);
        }

        if (this.navData?.fixes.high || this.navData?.fixes.low) {
            // If either one is selected also include B
            usageExpressions.push(['==', ['get', 'usage'], 'B']);
        }

        const filter: FilterSpecification = ['all', ['any', ...filterExpressions], ['any', ['!', ['has', 'usage']], ...usageExpressions]];

        this.setFilter('fix', filter);
        this.setPaintProperty('fix', 'icon-color', this.toColorExpression(colorExpressions, ['global-state', 'feNav_icon-color']));

        // Terminal fixes are part of a different source layer
        const terminalFilter = [] as ExpressionSpecification[];
        const terminalColor = [] as [ExpressionSpecification, string][];

        // Use global terminal color
        if (this.navData?.fixes.terminal.show) {
            terminalFilter.push(['literal', true]);
        }
        if (this.navData?.fixes.terminal.color) {
            terminalColor.push([['literal', true], this.navData.fixes.terminal.color]);
        }

        if (filterGroup?.show) {
            this.addFilterGroupToExpressions(filterGroup, ['get', 'id'], terminalFilter, terminalColor);
        }

        this.setFilter('fix_t', ['any', ...terminalFilter]);
        this.setPaintProperty('fix_t', 'icon-color', this.toColorExpression(terminalColor, ['global-state', 'feNav_icon-color']));
    }

    private setFlightServiceStationsFilter(): void {
        if (!this.map) {
            return;
        }

        const { filterGroup } = this.navData!.flightServiceStation;
        let filter: FilterSpecification = true;
        if (filterGroup?.show) {
            const values = this.getFilterGroupFlatMembers(filterGroup.ids);
            filter = ['in', ['get', 'id'], ['literal', values]];
        } else {
            filter = false;
        }

        this.setFilter('fss', filter);
    }

    private setHoldingPatternsFilter(): void {
        if (!this.map) {
            return;
        }

        const { filterGroup } = this.navData!.holding;
        let filter: FilterSpecification = false;

        for (const [key, value] of Object.entries(holdingTypes)) {
            if (this.navData!.holding[key as keyof IUDViewHoldingModel]) {
                filter = ['any', filter, ['==', ['get', 'type'], value]];
            }
        }

        if (filterGroup?.show) {
            const values = this.getFilterGroupFlatMembers(filterGroup.ids);
            filter = ['any', filter, ['in', ['get', 'fixid'], ['literal', values]]];
        }

        this.setFilter('holding', filter);
        this.setFilter('holding_label', filter);
    }

    private setNavaidsFilter(): void {
        if (!this.map) {
            return;
        }

        const { filterGroup } = this.navData!.navaids;
        const filterExpressions: ExpressionSpecification[] = [];
        const colorExpressions: [ExpressionSpecification, string][] = [];

        for (const [key, expression] of Object.entries(naviadFilterExpressions)) {
            const subType = this.navData!.navaids[key as keyof typeof naviadFilterExpressions];
            if (subType.show) {
                filterExpressions.push(expression);
            }

            if (subType.color) {
                colorExpressions.push([expression, subType.color]);
            }
        }

        if (filterGroup) {
            this.addFilterGroupToExpressions(filterGroup, ['get', 'id'], filterExpressions, colorExpressions);
        }

        const filter: FilterSpecification = ['any', ...filterExpressions];
        const color = this.toColorExpression(colorExpressions, ['global-state', 'feNav_icon-color']);

        this.setFilter('navaid', filter);
        this.setPaintProperty('navaid', 'icon-color', color);

        this.setFilter('navaid_rose', filter);
        this.setPaintProperty('navaid_rose', 'icon-color', color);
    }

    private setSidsFilter(): void {
        if (!this.map) {
            return;
        }

        const { filterGroup } = this.navData!.sids;
        const filter = this.getSidsStarsFilter(filterGroup);

        this.setFilter('sid', filter);
        this.setFilter('sid_point', filter);
        this.setFilter('sid_arrow', filter);
    }

    private setStarsFilter(): void {
        if (!this.map) {
            return;
        }

        const { filterGroup } = this.navData!.stars;
        const filter = this.getSidsStarsFilter(filterGroup);

        this.setFilter('star', filter);
        this.setFilter('star_point', filter);
        this.setFilter('star_arrow', filter);
    }

    private setSuaFilter(): void {
        if (!this.map) {
            return;
        }

        const { filterGroup } = this.navData!.sua;
        let filter: FilterSpecification = false;
        for (const [key, value] of Object.entries(suaTypes)) {
            if (this.navData!.sua[key as keyof IUDViewSUAModel]) {
                filter = ['any', filter, ['==', ['get', 'type'], value]];
            }
        }

        if (filterGroup?.show) {
            const values = this.getFilterGroupFlatMembers(filterGroup.ids);
            filter = ['any', filter, ['in', ['get', 'id'], ['literal', values]]];
        }

        this.setFilter('sua_border', filter);
        this.setFilter('sua_band', filter);
        this.setFilter('sua_label', filter);
    }

    private weatherStationsFilter(): void {
        if (!this.map) {
            return;
        }

        const filterGroup = this.navData!.weatherStation.filterGroup;
        let filter: FilterSpecification;
        if (filterGroup?.show) {
            const values = this.getFilterGroupFlatMembers(filterGroup.ids);
            filter = ['in', ['get', 'id'], ['literal', values]];
        } else {
            filter = true;
        }

        this.setFilter('wstation', filter);
    }

    private setLayoutProperty<K extends keyof AllLayoutProperties>(layerId: string, property: K, value: AllLayoutProperties[K]): void {
        if (!this.map || !this.map.getLayer(layerId)) {
            console.warn(`Layer ${layerId} not found`);
            return;
        }

        this.map.setLayoutProperty(layerId, property, value);
    }

    private setFilter(layerId: string, filter: FilterSpecification): void {
        if (!this.map || !this.map.getLayer(layerId)) {
            console.warn(`Layer ${layerId} not found`);
            return;
        }

        this.map.setFilter(layerId, filter);
    }

    private setPaintProperty<K extends keyof AllPaintProperties>(layerId: string, property: K, value: AllPaintProperties[K]): void {
        if (!this.map || !this.map.getLayer(layerId)) {
            console.warn(`Layer ${layerId} not found`);
            return;
        }

        this.map.setPaintProperty(layerId, property, value);
    }

    private toVisibility(value: boolean): 'visible' | 'none' {
        return value ? 'visible' : 'none';
    }

    private splitFilterValues(values: string[]): { wildcharts: string[]; ids: string[]; airports: string[] } {
        const wildcharts: string[] = [];
        const ids: string[] = [];
        const airports: string[] = [];
        for (const v of values) {
            if (v.startsWith('*.')) {
                wildcharts.push(v.substring(1));
            } else if (v.endsWith('.*')) {
                wildcharts.push(v.substring(0, v.length - 1));
            } else if (v.length >= 3 && v.length <= 4 && !v.includes('.')) {
                airports.push(v);
            } else {
                ids.push(v);
            }
        }
        return { wildcharts, ids, airports };
    }

    private getSidsStarsFilter(filterGroup: IUDViewFilterGroupModel | undefined): FilterSpecification {
        let filter: FilterSpecification = true;
        if (filterGroup?.show) {
            if (filterGroup?.show && filterGroup.ids.length <= 0) {
                filter = false;
            } else {
                const values = this.getFilterGroupFlatMembers(filterGroup.ids);
                const { wildcharts, ids, airports } = this.splitFilterValues(values);
                const idsfilter: FilterSpecification = ids.length > 0 ? ['in', ['get', 'id'], ['literal', ids]] : false;
                const airportfilter: FilterSpecification = airports.length > 0 ? ['in', ['get', 'aptid'], ['literal', airports]] : false;

                let wildchartfilter: FilterSpecification = false;
                if (wildcharts.length > 0) {
                    const f: FilterSpecification[] = [];
                    for (const wildchart of wildcharts) {
                        f.push(['in', wildchart, ['string', ['get', 'id']]]);
                    }
                    wildchartfilter = ['any', ...f] as FilterSpecification;
                }

                filter = ['any', idsfilter, airportfilter, wildchartfilter] as FilterSpecification;
            }
        }

        return filter;
    }

    private getFilterGroupFlatMembers(ids: string[]): string[] {
        return ids
            .map((x) => this.filterGroups[x])
            .filter((x) => !!x)
            .map((x) => x!.members.map((x) => x.text))
            .flat();
    }

    private addFilterGroupToExpressions(
        filterGroup: IUDViewFilterGroupModel,
        compareWithExpressions: ExpressionSpecification | ((elements: string[]) => ExpressionSpecification),
        filterExpressions: ExpressionSpecification[],
        colorExpressions: [ExpressionSpecification, string][]
    ) {
        if (filterGroup.show) {
            const values = this.getFilterGroupFlatMembers(filterGroup.ids);
            filterExpressions.push(
                typeof compareWithExpressions === 'function'
                    ? compareWithExpressions(values)
                    : ['in', compareWithExpressions, ['literal', values]]
            );

            for (const id of filterGroup.ids) {
                const group = this.filterGroups[id];
                if (!group) {
                    continue;
                }

                const color = filterGroup.config[id]?.color;

                if (!color) {
                    continue;
                }

                const members = group.members.map((x) => x.text);
                colorExpressions.unshift([
                    typeof compareWithExpressions === 'function'
                        ? compareWithExpressions(members)
                        : ['in', compareWithExpressions, ['literal', members]],
                    color
                ]);
            }
        }
    }

    private toColorExpression(
        exprs: [ExpressionSpecification, string][],
        defaultColor: ExpressionSpecification
    ): AllPaintProperties['icon-color'] {
        if (exprs.length === 0) {
            return defaultColor;
        }

        return ['case', ...exprs.flat(), defaultColor] as AllPaintProperties['icon-color'];
    }

    private fontSizeToScale(size: number, minIn: number, maxIn: number, minOut: number, maxOut: number): number {
        return ((size - minIn) / (maxIn - minIn)) * (maxOut - minOut) + minOut;
    }
}
